import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Link, useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';

import ActionButton from '../ActionButton/ActionButton';
import Modal from '../Modal/Modal';
import SelectOptions from '../SelectOptions/SelectOptions';

import translate from '../../libs/i18n';
import toTitleCase from '../../utils/toTitleCase';
import { reportShape } from '../../utils/propTypesShapes';
import { useBreakpoint } from '../../utils/useBreakpoint';

import Textarea from '../../components/Textarea/Textarea';
import ArrowIcon from '../../assets/svgs/ArrowIcon';
import CheckIcon from '../../assets/svgs/CheckIcon';
import ChevronIcon from '../../assets/svgs/ChevronIcon';
import EditIcon from '../../assets/svgs/EditIcon';

import { SingleReportHeaderStyle } from './SingleReportHeaderStyle';

const SingleReportHeader = ({
  examType,
  allReportsMini,
  currentReport,
  approvedReport,
  handleRequestReviewModal,
  handleApprovedReport,
  reportFeedbackComments,
  setReportFeedbackComments,
  canApproveReport,
}) => {
  const breakpoint = useBreakpoint();
  const history = useHistory();
  const language = useSelector((state) => state.settings.language);

  const [lastReportId, setLastReportId] = useState();
  const [nextReportId, setNextReportId] = useState();
  const [reportSelectOptions, setReportSelectOptions] = useState([]);
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  const [tempFeedbackComments, setTempFeedbackComments] = useState('');

  useEffect(() => {
    if (
      allReportsMini &&
      allReportsMini.length &&
      currentReport &&
      currentReport.id
    ) {
      const currentReportIndex = allReportsMini.findIndex(
        (report) => report.id === currentReport.id
      );

      if (currentReportIndex > 0) {
        setLastReportId(allReportsMini[currentReportIndex - 1].id);
      } else {
        setLastReportId(-1);
      }

      if (currentReportIndex < allReportsMini.length - 1) {
        setNextReportId(allReportsMini[currentReportIndex + 1].id);
      } else {
        setNextReportId(-1);
      }
    }
  }, [allReportsMini, currentReport]);

  useEffect(() => {
    if (allReportsMini?.length && currentReport?.id) {
      const currentReportSelectOptions = [];
      
      const validReports = allReportsMini.filter(
        (report) => report.status && report.status !== 'ERR'
      );

      validReports.forEach((report) => {
        if (report?.id && report?.report_name?.length) {
          let text = report.report_name;

          if (/Computed Radiography/i.test(text)) {
            text = text.replace(/Computed Radiography/i, translate('cr', language));
          } else if (/Computed Tomography/i.test(text)) {
            text = text.replace(/Computed Tomography/i, translate('ct', language));
          }

          currentReportSelectOptions.push({
            key: report.id,
            value: report.id,
            text,
          });
        }
      });
      setReportSelectOptions(currentReportSelectOptions);
    }
  }, [allReportsMini, currentReport, language]);

  const approveResultsButtonText = useMemo(() => {
    const start = approvedReport ? 'approved' : 'approve';
    const end =
      breakpoint &&
      breakpoint.name !== 'iphone' &&
      breakpoint.name !== 'iphone8' &&
      breakpoint.name !== 'iphone-plus'
        ? 'Results'
        : '';

    return translate(`${start}${end}`, language);
  }, [approvedReport, breakpoint, language]);

  const handleChangeReport = (id) => {
    history.push(`/relatorios/${examType}/${id}`);
    history.go(`/relatorios/${examType}/${id}`);
  };

  const disabledApproveAndRequestReview = useMemo(() => {
    if (!approvedReport && (currentReport && currentReport.feedback_count > 0)) {
      return true;
    }

    return false;
  }, [approvedReport, currentReport]);

  return (
    <SingleReportHeaderStyle>
      <Link to="/" className="back-link">
        <span className="icon">
          <ArrowIcon />
        </span>
        {translate('back', language)}
      </Link>
      <div className="header-tools">
        <div className="select-report-area">
          {reportSelectOptions && reportSelectOptions.length ? (
            <SelectOptions
              theme="dark"
              options={reportSelectOptions}
              value={currentReport.id}
              onChange={handleChangeReport}
            />
          ) : null}
        </div>
        {canApproveReport ? (
          <div className="approve-review-area">
            <div className="agree-with-results-ai">
              {translate('agreeWithResultsAi', language)}
            </div>
            <div className="approve-results-button">
              <ActionButton
                theme={approvedReport ? 'default' : 'light'}
                icon={approvedReport ? <CheckIcon /> : null }
                text={approveResultsButtonText}
                transition
                disabled={approvedReport}
                onClick={() => setShowConfirmModal(true)}
              />
            </div>
            <div className="request-review-button">
              <ActionButton
                theme={disabledApproveAndRequestReview ? 'default' : 'light'} 
                icon={disabledApproveAndRequestReview ? <CheckIcon /> : null }
                text={disabledApproveAndRequestReview ? translate('changeReview', language) :  translate('requestReview', language) }
                transition
                onClick={handleRequestReviewModal}
              />
            </div>
          </div>
        ) : null}
        <div className="edit-and-navigation-area">
          <div className="edit-button-wrapper">
            <ActionButton
              theme="very-dark"
              text={
                breakpoint && breakpoint.name !== 'ipad-landscape' ? 'Edit' : ''
              }
              icon={<EditIcon />}
              disabled={lastReportId <= -1}
              onClick={() => handleChangeReport(lastReportId)}
            />
          </div>
          <div className="navigation-wrapper">
            <div className="navigation-link last-navigation-link">
              <ActionButton
                theme="very-dark"
                icon={<ChevronIcon style={{ transform: 'rotate(90deg)' }} />}
                text={
                  breakpoint === 'hd' ? translate('previous', language) : ''
                }
                disabled={lastReportId <= -1}
                onClick={() => handleChangeReport(lastReportId)}
              />
            </div>
            <div className="navigation-link next-navigation-link">
              <ActionButton
                theme="very-dark"
                icon={<ChevronIcon style={{ transform: 'rotate(-90deg)' }} />}
                text={breakpoint === 'hd' ? translate('next', language) : ''}
                disabled={nextReportId <= -1}
                onClick={() => handleChangeReport(nextReportId)}
                iconPosition="right"
              />
            </div>
          </div>
        </div>
      </div>

      {showConfirmModal ? (
        <Modal theme="light" handleClose={() => setShowConfirmModal(false)}>
          <div className="confirm-report-modal-container">
            <h3 className="report-modal-title">
              {toTitleCase(translate('approveResults', language))}
            </h3>
            <p className="report-modal-question">
              {translate('areYouSureAgreeWithResultsByAi', language)}
            </p>
            <div className="review-modal-about-desagree">
                <p className="report-modal-question">
                  {translate('registerSomeObservation', language)}.
                </p>
                <Textarea
                  value={tempFeedbackComments}
                  handleChange={(event) =>
                    setTempFeedbackComments(event.currentTarget.value)
                  }
                  placeholder={translate('typeYourFeedback', language)}
                />
              <p className="report-modal-description">
                {translate(
                  'whenAgreeResultsMeansAgreeWithSegmentationAndClassification',
                  language
                )}
              </p>
            </div>
            <div className="report-modal-actions">
              <div className="modal-action-wrapper">
                <ActionButton
                  theme="light-disabled"
                  text={translate('cancel', language).toUpperCase()}
                  onClick={() => setShowConfirmModal(false)}
                />
              </div>
              <div className="modal-action-wrapper">
                <ActionButton
                  theme="default"
                  text={translate('yesIDo', language).toUpperCase()}
                  onClick={() => {
                    setReportFeedbackComments(tempFeedbackComments);
                    setShowConfirmModal(false);
                    handleApprovedReport(tempFeedbackComments);
                  }}
                />
              </div>
            </div>
          </div>
        </Modal>
      ) : null}
    </SingleReportHeaderStyle>
  );
};

SingleReportHeader.propTypes = {
  examType: PropTypes.string,
  allReportsMini: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      report_name: PropTypes.string,
    })
  ),
  currentReport: reportShape.isRequired,
  approvedReport: PropTypes.bool,
  handleRequestReviewModal: PropTypes.func.isRequired,
  handleApprovedReport: PropTypes.func.isRequired,
  reportFeedbackComments: PropTypes.string.isRequired,
  setReportFeedbackComments: PropTypes.func.isRequired,
  canApproveReport: PropTypes.bool,
};

SingleReportHeader.defaultProps = {
  allReportsMini: [],
  approvedReport: false,
  canApproveReport: false,
};

export default SingleReportHeader;
